import { useQuery } from "@tanstack/react-query";
import { workflowContainerFullApi } from "./axios";
import {
  DataI,
  ServiceResponseI,
  PropsWorkflowContainerFullI,
  WorkflowContainerFullI,
  ObjPostI,
  StatisticsWorkflowContainerFullI,
  ResponseStatisticsContainerFullI,
  EnhancedServiceResponseI,
} from "../../interfaces";
import {
  ValidateCurrentToken,
  LogoutSystem,
  generateOfflineId,
  validateServiceWorker,
} from "../../helpers";
import { AxiosError } from "axios";
import { useRouterStore } from "../../stores";

const getWorkflowContainerFulls = async ({
  pagination,
  sorting,
  globalFilter,
  producer,
  farm,
  client,
  range,
  rangeVal,
}: PropsWorkflowContainerFullI): Promise<
  ServiceResponseI<DataI<WorkflowContainerFullI>>
> => {
  try {
    const validateToken = ValidateCurrentToken();

    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    params.append("page", (pagination!.pageIndex + 1).toString());
    params.append("limit", pagination!.pageSize.toString());
    params.append("search", globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    client && params.append("client", client.toString());
    producer && params.append("producer", producer.toString());
    farm && params.append("farm", farm.toString());
    rangeVal && params.append("range", JSON.stringify(range));

    const { data } = await workflowContainerFullApi.get<
      ServiceResponseI<DataI<WorkflowContainerFullI>>
    >("", { params, headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    console.log(err);
    const obj = (err as AxiosError).response;

    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }

    throw new Error((obj?.data as Error).message);
  }
};

const getStatisticsContainerFulls = async ({
  producer,
  farm,
  client,
  box,
  brand,
  range,
  date,
  type,
}: StatisticsWorkflowContainerFullI): Promise<
  ServiceResponseI<ResponseStatisticsContainerFullI>
> => {
  try {
    const validateToken = ValidateCurrentToken();

    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    type && params.append("type", type.toString());
    client && params.append("client", client.toString());
    producer && params.append("producer", producer.toString());
    farm && params.append("farm", farm.toString());
    range && params.append("range", range.toString());
    date && params.append("date", date.toString());
    box && params.append("box", box.toString());
    brand && params.append("brand", brand.toString());

    const { data } = await workflowContainerFullApi.get<
      ServiceResponseI<ResponseStatisticsContainerFullI>
    >("statistics", { params, headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    console.log(err);
    const obj = (err as AxiosError).response;

    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }

    throw new Error((obj?.data as Error).message);
  }
};

export const getAllWorkflowContainerFulls = async (): Promise<
  ServiceResponseI<WorkflowContainerFullI[]>
> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await workflowContainerFullApi.get<
      ServiceResponseI<WorkflowContainerFullI[]>
    >(`/all-no-pagination`, { headers: { Authorization: validateToken } });

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowContainerFullForExternalById = async (
  id: string
): Promise<ServiceResponseI<WorkflowContainerFullI>> => {
  try {
    // const validateToken = ValidateCurrentToken();
    // if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowContainerFullApi.get<
      ServiceResponseI<WorkflowContainerFullI>
    >(
      `/external/${id}`
      // { headers: { Authorization: validateToken } }
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowContainerFullPost = async <T>(
  getForms: T
): Promise<EnhancedServiceResponseI<ObjPostI>> => {


  const { addProcessedContainer, byPassSw } = useRouterStore.getState();

  let result: EnhancedServiceResponseI<ObjPostI>;

  const validateToken = ValidateCurrentToken();
  if (!validateToken) throw new Error("TOKEN INVALIDO");

  const containerNumber = (getForms as any)?.container;

  // if (containerNumber && isContainerProcessedToday(containerNumber, "full")) {
  //   console.log(
  //     `🔄 Contenedor ${containerNumber} (lleno) ya fue procesado hoy`
  //   );

  //   const duplicateResponse: EnhancedServiceResponseI<ObjPostI> = {
  //     statusCode: 200,
  //     data: {
  //       id: 0,
  //       alert: `Contenedor ${containerNumber} (lleno) ya fue registrado hoy`,
  //       existed: true,
  //       created: false,
  //       variant: "warning" as const,
  //       status: "active",
  //     },
  //     message: "Duplicado de contenedor lleno detectado",
  //     res: {} as Response,
  //     req: {} as Request,
  //     isOffline: false,
  //     isDuplicate: true,
  //     duplicateInfo: {
  //       existingId: 0,
  //       originalDate: new Date().toISOString(),
  //       status: "processed-today-full",
  //     },
  //     offlineId: generateOfflineId("full"),
  //     processingTime: 0,
  //   };

  //   return duplicateResponse;
  // }

  if (byPassSw) {
    result = await performDirectRequest(getForms, validateToken);
    return result;
  }

  const offlineId = generateOfflineId("full");
  const url = `${process.env.REACT_APP_BACKEND}api/workflow-container-full`;

  const swReady = await validateServiceWorker();

  if (!swReady) {
    result = await performDirectRequest(getForms, validateToken);
  } else {
    result = await performServiceWorkerRequest(
      url,
      getForms,
      validateToken,
      offlineId
    );
  }

  if (
    result.data &&
    !result.hasError &&
    containerNumber &&
    result.data.created !== false
  ) {
    addProcessedContainer(containerNumber, "full");
  }

  return result;
};

async function performServiceWorkerRequest<T>(
  url: string,
  getForms: T,
  validateToken: string,
  offlineId: string
): Promise<EnhancedServiceResponseI<ObjPostI>> {
  return new Promise((resolve, reject) => {
    const messageChannel = new MessageChannel();
    let isResolved = false;

    messageChannel.port1.onmessage = (event) => {
      if (isResolved) return;

      // clearTimeout(timeout);
      isResolved = true;

      console.log(`📬 Respuesta SW para ${offlineId}:`, event.data);

      if (event.data.error) {
        console.error(`❌ Error SW: ${event.data.error}`);
        reject(new Error(event.data.error));
        return;
      }

      if (event.data.saved !== true) {
        console.warn(`⚠️ SW no confirmó guardado para ${offlineId}`);
        reject(
          new Error("Service Worker no confirmó que los datos se guardaron")
        );
        return;
      }

      if (event.data.directSend) {
        resolve({
          data: {
            id: Date.now(),
            alert: "Proceso Guardado con éxito!",
            variant: navigator.onLine ? "info" : "success",
          },
          res: {} as any,
          req: {} as any,
          isOffline: true,
          swResponse: event.data,
          offlineId,
        });
        return;
      }

      resolve({
        data: {
          id: Date.now(),
          alert: navigator.onLine
            ? "Datos encolados, sincronizando..."
            : "Datos guardados offline",
          variant: navigator.onLine ? "info" : "success",
        },
        res: {} as any,
        req: {} as any,
        isOffline: true,
        swResponse: event.data,
        offlineId,
      });
    };

    // ===== ENVÍO AL SERVICE WORKER =====
    const messageData = {
      type: "ENQUEUE_POST",
      url,
      data: getForms,
      offlineId,
      timestamp: Date.now(), // Para debugging
      headers: {
        "Content-Type": "application/json",
        Authorization: validateToken,
        "X-Offline-ID": offlineId,
        "X-Client-Type": "mobile-pwa", // Identificador para móviles
      },
    };

    try {
      const swController = navigator.serviceWorker.controller;
      if (!swController) {
        reject(
          new Error("Service Worker controller perdido durante la operación")
        );
        return;
      }

      swController.postMessage(messageData, [messageChannel.port2]);
      console.log(`📨 Mensaje enviado al SW para ${offlineId}`);
    } catch (error: any) {
      console.error(`❌ Error enviando al SW:`, error);
      reject(
        new Error(`Error comunicándose con Service Worker: ${error.message}`)
      );
    }
  });
}

async function performDirectRequest<T>(
  getForms: T,
  validateToken: string
): Promise<EnhancedServiceResponseI<ObjPostI>> {
  try {
    const directResponse = await workflowContainerFullApi.post<
      ServiceResponseI<ObjPostI>
    >("", getForms, {
      headers: { Authorization: validateToken },
      timeout: 60000, // Reducido para móviles
    });

    return directResponse.data;
  } catch (directError) {
    console.error("❌ Error en petición directa:", directError);
    throw new Error(
      "Service Worker no disponible y petición directa falló. Verifique su conexión."
    );
  }
}

export const workflowContainerFullPut = async <T>(
  id: number,
  updateForms: T
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowContainerFullApi.put<
      ServiceResponseI<string>
    >(`/${id}`, updateForms, { headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowContainerFullGenerateExcelById = async ({
  client,
  producer,
  farm,
  box,
  brand,
  range,
  date,
  type,
}: StatisticsWorkflowContainerFullI): Promise<
  ServiceResponseI<WorkflowContainerFullI>
> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    type && params.append("type", type.toString());
    client && params.append("client", client.toString());
    producer && params.append("producer", producer.toString());
    farm && params.append("farm", farm.toString());
    range && params.append("range", range.toString());
    date && params.append("date", date.toString());
    box && params.append("box", box.toString());
    brand && params.append("brand", brand.toString());

    const { data } = await workflowContainerFullApi.get<
      ServiceResponseI<WorkflowContainerFullI>
    >(`/generate-excel`, { params, headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowContainerFullById = async (
  id: number
): Promise<ServiceResponseI<WorkflowContainerFullI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowContainerFullApi.get<
      ServiceResponseI<WorkflowContainerFullI>
    >(`/${id}`, { headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowContainerFullByContainer = async (
  container: string
): Promise<ServiceResponseI<WorkflowContainerFullI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowContainerFullApi.get<
      ServiceResponseI<WorkflowContainerFullI>
    >(`/by-container/${container}`, {
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowContainerFullForCPByContainer = async (
  container: string
): Promise<ServiceResponseI<WorkflowContainerFullI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowContainerFullApi.get<
      ServiceResponseI<WorkflowContainerFullI>
    >(`/by-container-for-checkpoint/${container}`, {
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowContainerFullDelete = async (
  id: number
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowContainerFullApi.delete<
      ServiceResponseI<string>
    >(`/${id}`, { headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const useWorkflowContainerFulls = ({
  sorting,
  pagination,
  globalFilter,
  producer,
  farm,
  range,
  rangeVal,
}: PropsWorkflowContainerFullI) => {
  const workflowContainerFullsQuery = useQuery(
    [
      "workflowContainerFulls",
      { pagination, sorting, globalFilter, producer, farm, range, rangeVal },
    ],
    () =>
      getWorkflowContainerFulls({
        pagination,
        sorting,
        globalFilter,
        producer,
        farm,
        range,
        rangeVal,
      }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  return {
    workflowContainerFullsQuery,
  };
};

export const useStatisticsContainerFull = ({
  client,
  producer,
  farm,
  box,
  brand,
  range,
  date,
  type,
}: StatisticsWorkflowContainerFullI) => {
  const statisticsContainerFullsQuery = useQuery(
    [
      "statisticsContainerFulls",
      { producer, farm, box, brand, range, date, type, client },
    ],
    () =>
      getStatisticsContainerFulls({
        producer,
        farm,
        box,
        brand,
        range,
        date,
        type,
        client,
      }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  return {
    statisticsContainerFullsQuery,
  };
};

export const generateWfFullPdfReportbyId = async (
  id: number
): Promise<ServiceResponseI<any>> => {
  try {
    // const validateToken = ValidateCurrentToken();
    // if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await workflowContainerFullApi.get<ServiceResponseI<any>>(
      `/generate-pdf-report/${id}`
      // { headers: { Authorization: validateToken } }
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const generateWfFullTarjaTextbyId = async (
  id: number
): Promise<ServiceResponseI<any>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await workflowContainerFullApi.get<ServiceResponseI<any>>(
      `/tarja-text/${id}`,
      { headers: { Authorization: validateToken } }
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const downloadWfFullFilesById = async (
  id: number
): Promise<ServiceResponseI<any>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await workflowContainerFullApi.get<ServiceResponseI<any>>(
      `/download-full-media/${id}`,
      { headers: { Authorization: validateToken }, responseType: "blob" }
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const generateWfFullPdfTarjabyId = async (
  id: number
): Promise<ServiceResponseI<any>> => {
  try {
    // const validateToken = ValidateCurrentToken();
    // if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await workflowContainerFullApi.get<ServiceResponseI<any>>(
      `/generate-pdf-tarja/${id}`
      // { headers: { Authorization: validateToken } }
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowContainerFullCount = async (
  type = "hoy"
): Promise<ServiceResponseI<{ total: number }>> => {
  try {
    const params = new URLSearchParams();

    type && params.append("type", type.toString());

    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowContainerFullApi.get<
      ServiceResponseI<{ total: number }>
    >(`/count`, { params, headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowFullSendReportById = async <T>(
  getForms: T
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowContainerFullApi.post<
      ServiceResponseI<string>
    >(`/send-report-by-workflow-id`, getForms, {
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};
